import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { getComponentElements } from './utils.js';
import ChildrenManager from './managers/ChildrenManager.js';
import RefsManager from './managers/RefsManager.js';
import ServicesManager from './managers/ServicesManager.js';
import EventsManager from './managers/EventsManager.js';
import OptionsManager from './managers/OptionsManager.js';
var id = 0;

function noop() {}

export default class Base {
  get $root() {
    if (!this.$parent) {
      return this;
    }

    var parent = this.$parent;
    var root = this.$parent;

    while (parent) {
      if (!parent.$parent) {
        root = parent;
      }

      parent = parent.$parent;
    }

    return root;
  }

  get __config() {
    var proto = Object.getPrototypeOf(this);
    var {
      config
    } = proto.constructor;

    while (proto.constructor.config && proto.constructor.$isBase) {
      config = _objectSpread(_objectSpread({}, proto.constructor.config), config);

      if (proto.constructor.config.options) {
        config.options = _objectSpread(_objectSpread({}, proto.constructor.config.options), config.options);
      }

      proto = Object.getPrototypeOf(proto);
    }

    return config;
  }

  get $services() {
    var services = this.__services;
    this.$emit('get:services', services);
    return services;
  }

  get $refs() {
    var refs = this.__refs;
    this.$emit('get:refs', refs);
    return refs;
  }

  get $options() {
    var options = this.__options;
    this.$emit('get:options', options);
    return options;
  }

  get $children() {
    var children = this.__children;
    this.$emit('get:children', children);
    return children;
  }

  get $log() {
    return this.__options.log ? window.console.log.bind(window, "[".concat(this.__config.name, "]")) : noop;
  }

  get __debug() {
    return this.__options.debug && typeof window.__DEV__ !== 'undefined' && window.__DEV__ ? window.console.log.bind(window, "[debug] [".concat(this.$id, "]")) : noop;
  }

  __callMethod(method) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    this.__debug('callMethod', method, ...args);

    if (method === 'destroyed' && !this.$isMounted || method === 'mounted' && this.$isMounted) {
      this.__debug('not', method, 'because the method has already been triggered once.');

      return;
    }

    this.$emit(method, ...args);

    if (typeof this[method] !== 'function') {
      return;
    }

    this[method].call(this, ...args);

    this.__debug(method, this, ...args);
  }

  constructor(element) {
    _defineProperty(this, "$parent", null);

    _defineProperty(this, "$id", void 0);

    _defineProperty(this, "$el", void 0);

    _defineProperty(this, "$isMounted", false);

    _defineProperty(this, "__services", void 0);

    _defineProperty(this, "__refs", void 0);

    _defineProperty(this, "__options", void 0);

    _defineProperty(this, "__children", void 0);

    _defineProperty(this, "__events", void 0);

    if (!element) {
      throw new Error('The root element must be defined.');
    }

    var {
      __config
    } = this;

    if (!__config) {
      throw new Error('The `config` static property must be defined.');
    }

    if (!__config.name) {
      throw new Error('The `config.name` property is required.');
    }

    this.$id = "".concat(__config.name, "-").concat(id);
    id += 1;
    this.$el = element;

    if (!('__base__' in this.$el)) {
      Object.defineProperty(this.$el, '__base__', {
        get: () => this,
        configurable: true
      });
    }

    this.__options = new OptionsManager(element, __config.options || {}, __config);
    this.__services = new ServicesManager(this);
    this.__events = new EventsManager(element, this);
    this.__children = new ChildrenManager(this, element, __config.components || {}, this.__events);
    this.__refs = new RefsManager(this, element, __config.refs || [], this.__events);

    this.__debug('constructor', this);

    return this;
  }

  $mount() {
    this.__debug('$mount');

    this.$children.registerAll();
    this.$refs.registerAll();

    this.__events.bindRootElement();

    this.$services.enableAll();
    this.$children.mountAll();

    this.__callMethod('mounted');

    this.$isMounted = true;
    return this;
  }

  $update() {
    this.__debug('$update');

    this.$refs.unregisterAll();
    this.$services.disableAll();
    this.$children.registerAll();
    this.$refs.registerAll();
    this.$services.enableAll();
    this.$children.updateAll();

    this.__callMethod('updated');

    return this;
  }

  $destroy() {
    this.__debug('$destroy');

    this.__events.unbindRootElement();

    this.$refs.unregisterAll();
    this.$services.disableAll();
    this.$children.destroyAll();

    this.__callMethod('destroyed');

    this.$isMounted = false;
    return this;
  }

  $terminate() {
    this.__debug('$terminate');

    this.$destroy();

    this.__callMethod('terminated');

    Object.defineProperty(this.$el, '__base__', {
      value: 'terminated',
      configurable: false,
      writable: false
    });
  }

  static $factory(nameOrSelector) {
    if (!nameOrSelector) {
      throw new Error('The $factory method requires a component’s name or selector to be specified.');
    }

    return getComponentElements(nameOrSelector).map(el => new this(el).$mount());
  }

  $on(event, listener, options) {
    this.__debug('$on', event, listener, options);

    this.$el.addEventListener(event, listener, options);
    return () => {
      this.$off(event, listener, options);
    };
  }

  $off(event, listener, options) {
    this.__debug('$off', event, listener);

    this.$el.removeEventListener(event, listener, options);
  }

  $emit(event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    this.__debug('$emit', event, args);

    this.$el.dispatchEvent(new CustomEvent(event, {
      detail: args
    }));
  }

}

_defineProperty(Base, "$isBase", true);

_defineProperty(Base, "config", void 0);